import React, { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { ILocaleImageFragment } from 'generated/sanity-graphql';
import useMediaQuery from 'hooks/use-media-query';
import { appHeaderSelector } from 'pages/loyalty/loyalty-banner/constants';
import LoyaltyStickyBanner from 'pages/loyalty/loyalty-banner/loyalty-sticky-banner';
import AuthenticatedTiersBanner from 'pages/loyalty/loyalty-banner/loyalty-tiers-banner-authenticated';
import UnauthenticatedTiersBanner from 'pages/loyalty/loyalty-banner/loyalty-tiers-banner-unauthenticated';
import { BannerContainer, EmptySpacer } from 'pages/loyalty/loyalty-banner/styled';
import { loyaltyBannerObserverOptions } from 'pages/loyalty/loyalty-banner/utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';

export type ILoyaltyBannerWidgetProps = {
  enabled?: boolean;
  unauthenticatedDesktopBackgroundImage?: ILocaleImageFragment;
  unauthenticatedMobileBackgroundImage?: ILocaleImageFragment;
};

const Container = styled(BannerContainer)`
  width: 100%;
  padding: 0;
  margin-block-end: 1rem;

  ${Styles.desktop} {
    margin-block-end: 2rem;
  }
`;

export const UnauthenticatedContainer = styled.div<{ backgroundImage: string }>`
  width: 100%;
  min-height: 22rem;
  background-image: ${p => `url(${p.backgroundImage})`};
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  padding-block-start: 0.75rem;
  padding-block-end: 2rem;
  padding-inline: 1rem;

  ${Styles.desktop} {
    min-height: 40rem;
  }
`;

export const AuthenticatedContainer = styled.div`
  padding-block-start: 0.75rem;
  padding-block-end: 0.25rem;
  padding-inline: 1rem;
`;

export const SignUpButton = styled(ActionButton)`
  font-family: ${Styles.fontFamily.body};
  font-size: 1rem;
  width: 100%;
  text-transform: none;
  color: ${primitive.$white};
  border-color: ${primitive.$white};
  background-color: ${Styles.color.contrastBackground};
  padding: 0.575rem 0.75rem;

  ${Styles.desktop} {
    width: 600px;
    margin: 0 auto;
  }
`;

const LoyaltyBannerWidget: React.FC<ILoyaltyBannerWidgetProps> = ({
  enabled: signupButtonEnabled,
  unauthenticatedDesktopBackgroundImage,
  unauthenticatedMobileBackgroundImage,
}) => {
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser, loading: loyaltyLoading } = useLoyaltyUser();
  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();
  const { buildImageUrl } = useUIContext();
  const isMobile = useMediaQuery('mobile');
  const { formatMessage } = useIntl();
  const location = useLocation();
  const [showCompactBanner, setShowCompactBanner] = useState(false);

  const { enableLoyaltyTiers } = useLoyaltyRewards(loyaltyUser);
  const enableLoyaltyTiersFlag = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_TIERS);

  const loyaltyBannerRef = useRef<HTMLDivElement>(null);
  const portalRoot = document.querySelector(appHeaderSelector);

  useEffect(() => {
    const bannerEl = loyaltyBannerRef.current;

    if (!isMobile) {
      return;
    }

    const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      setShowCompactBanner(!entries[0].isIntersecting);
    }, loyaltyBannerObserverOptions);

    if (bannerEl) {
      observer.observe(bannerEl);
    }

    return () => {
      if (bannerEl) {
        observer.unobserve(bannerEl);
      }
    };
  }, [isMobile, userTier]);

  if (!enableLoyaltyTiers || !enableLoyaltyTiersFlag || !loyaltyEnabled || loyaltyLoading) {
    return null;
  }

  return (
    <Container data-testid="loyalty-tiers-how-it-works" ref={loyaltyBannerRef}>
      {isMobile &&
        portalRoot &&
        createPortal(
          <LoyaltyStickyBanner
            visible={showCompactBanner}
            displayRewardsLink={true}
            displaySignupLink={true}
          />,
          portalRoot
        )}
      {!showCompactBanner ? (
        <>
          {loyaltyUser ? (
            <AuthenticatedContainer>
              <AuthenticatedTiersBanner displayRewardsLink={true} />
            </AuthenticatedContainer>
          ) : (
            <UnauthenticatedContainer
              backgroundImage={
                isMobile
                  ? buildImageUrl(unauthenticatedMobileBackgroundImage?.locale!)
                  : buildImageUrl(unauthenticatedDesktopBackgroundImage?.locale!)
              }
            >
              <UnauthenticatedTiersBanner hideActionButton={true} displayCarousel={false} />

              {signupButtonEnabled ? (
                <SignUpButton
                  variant={ActionButtonVariants.OUTLINE}
                  size={ActionButtonSizes.SMALL}
                  to={routes.signUp}
                  data-testid="loyalty-tiers-how-it-works-signup"
                  eventAttributes={{
                    Name: 'Sign Up',
                    Path: location.pathname,
                  }}
                >
                  {formatMessage({ id: 'signUp' })}
                </SignUpButton>
              ) : null}
            </UnauthenticatedContainer>
          )}
        </>
      ) : (
        <EmptySpacer hasCarousel={true} />
      )}
    </Container>
  );
};

export default LoyaltyBannerWidget;
